import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'

import { PackagingLotStockInForm } from './new'
import { packagingNoteItemOption } from '@/components/lots/lot-option-builders'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import {
    usePackagingInNote,
    useUpdatePackagingInNote,
} from '@/hooks/use-packaging-lot-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/packaging-warehouse/stock-in-notes/$id_/edit',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data, isLoading } = usePackagingInNote(id)
    const note = data?.data
    const update = useUpdatePackagingInNote()

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isCreator = !!user?.id && note.created_by_id === user.id
    const editable =
        note.status === 'pending' && isCreator && !note.stocktake_session_id

    if (!editable) {
        return (
            <div className="space-y-4 p-6">
                <Button asChild variant="ghost" className="-ml-2">
                    <Link
                        to="/packaging-warehouse/stock-in-notes/$id"
                        params={{ id }}
                    >
                        <ArrowLeft className="size-4 mr-1" />
                        {m.sl_lot_back()}
                    </Link>
                </Button>
                <p className="text-muted-foreground">
                    {m.pl_pnk_edit_not_allowed()}
                </p>
            </div>
        )
    }

    // Mọi item map thành row 'existing' trỏ vào lô của phiếu; thêm lô mới (theo
    // vật tư) chỉ khi user thêm dòng mới trong lúc sửa.
    const initialValues = {
        reason: note.reason,
        notes: note.notes ?? '',
        items: (note.items ?? []).map((it) => ({
            kind: 'existing' as const,
            packaging_lot_id: it.packaging_lot_id,
            quantity: it.quantity,
        })),
    }

    // Lô lot-mode do PNK tạo đang 'pending' nên query lô active không trả về →
    // seed để ô "Chọn lô" hiển thị đúng nhãn.
    const seedLotOptions = (note.items ?? []).map(packagingNoteItemOption)

    const backToDetail = () =>
        navigate({
            to: '/packaging-warehouse/stock-in-notes/$id',
            params: { id },
        })

    return (
        <PackagingLotStockInForm
            mode="edit"
            title={m.pkl_pnk_edit_title()}
            submitLabel={m.pl_pnk_edit_submit()}
            initialValues={initialValues}
            seedLotOptions={seedLotOptions}
            isPending={update.isPending}
            onCancel={() => void backToDetail()}
            onSubmit={(value) => {
                update.mutate(
                    { id, req: value as never },
                    {
                        onSuccess: () => void backToDetail(),
                        onError: (err) =>
                            toast.error(
                                err instanceof Error
                                    ? err.message
                                    : m.sl_new_error_create(),
                            ),
                    },
                )
            }}
        />
    )
}
